import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useFavorites } from '../../hooks/useFavorites';
import { useChat } from '../../hooks/useChat';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import SaudiRiyalSymbol from '../../components/base/icons/SaudiRiyalSymbol';
import { apiRequest } from '../../utils/apiClient';
import { formatAmount, latnLocale } from '../../utils/numberFormat';
import { GooeyLoader } from '../../components/ui/gooey-loader';
import AvatarUpload from '../../components/profile/AvatarUpload';
import { COUNTRIES } from '../../config/countries';

const PROFILE_CURRENT_YEAR = new Date().getFullYear();
const PROFILE_BIRTH_YEARS = Array.from({ length: 83 }, (_, i) => PROFILE_CURRENT_YEAR - 10 - i);

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  gender: string;
  nationality: string;
  birthYear: string;
}

interface ProfileErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  gender?: string;
  nationality?: string;
  birthYear?: string;
  general?: string;
}

interface FavoritePropertyDetails {
  id: string;
  propertyId: string;
  title: string;
  city: string;
  state: string;
  price: number;
  imageUrl: string | null;
  createdAt: string;
}

export default function ProfilePage() {
  const { t, i18n } = useTranslation(['profile', 'common', 'registration']);
  const { user, updateProfile, signOut, session } = useAuth();
  const { favorites, loading: favoritesLoading, removeFavorite } = useFavorites();
  const { openChat } = useChat();
  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    email: '',
    gender: '',
    nationality: '',
    birthYear: ''
  });
  const [errors, setErrors] = useState<ProfileErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'favorites'>('profile');
  const [removingFavorite, setRemovingFavorite] = useState<string | null>(null);
  const [favoriteProperties, setFavoriteProperties] = useState<FavoritePropertyDetails[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  // Fetch property details for favorites from backend
  const fetchFavoriteProperties = useCallback(async () => {
    if (favorites.length === 0) {
      setFavoriteProperties([]);
      return;
    }

    setLoadingProperties(true);
    try {
      // Try to get favorites with property details from the backend
      if (session?.token) {
        const response = await apiRequest<{
          data: Array<{
            id: string;
            propertyId: string;
            createdAt: string;
            property?: {
              id: string;
              title: string;
              titleAr?: string;
              price: number;
              images?: Array<{ url: string }>;
              city?: { nameEn: string; nameAr: string };
              area?: { nameEn: string; nameAr: string };
            };
          }>;
          meta?: { total: number };
        }>('/favorites?page=1&pageSize=100', { token: session.token });

        const favoritesData = response.data || [];
        const propertiesWithDetails = favoritesData
          .filter(fav => fav.property)
          .map(fav => {
            const prop = fav.property!;
            const cityName = i18n.language === 'ar' ? prop.city?.nameAr : prop.city?.nameEn;
            const areaName = i18n.language === 'ar' ? prop.area?.nameAr : prop.area?.nameEn;
            const imageUrl = prop.images && prop.images.length > 0 ? prop.images[0].url : null;

            return {
              id: fav.id,
              propertyId: fav.propertyId,
              title: i18n.language === 'ar' && prop.titleAr ? prop.titleAr : prop.title || '',
              city: cityName || '',
              state: areaName || '',
              price: prop.price || 0,
              imageUrl,
              createdAt: fav.createdAt,
            };
          });

        setFavoriteProperties(propertiesWithDetails);
      }
    } catch (error) {
      console.error('Failed to fetch favorite properties:', error);
      // If API fails, show empty - we don't have property details
      setFavoriteProperties([]);
    } finally {
      setLoadingProperties(false);
    }
  }, [favorites, session?.token, i18n.language]);

  // Fetch favorite properties when favorites change
  useEffect(() => {
    fetchFavoriteProperties();
  }, [fetchFavoriteProperties]);

  const handleRemoveFavorite = async (propertyId: string) => {
    setRemovingFavorite(propertyId);
    try {
      await removeFavorite(propertyId);
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    } finally {
      setRemovingFavorite(null);
    }
  };

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user.email || '',
        gender: user?.gender || '',
        nationality: user?.nationality || '',
        birthYear: user?.birthYear ? String(user.birthYear) : ''
      });
    }
  }, [user]);

  const validateForm = (): boolean => {
    const newErrors: ProfileErrors = {};

    // First name validation
    if (!profileData.firstName.trim()) {
      newErrors.firstName = t('profile.firstNameRequired');
    } else if (profileData.firstName.trim().length < 2) {
      newErrors.firstName = t('profile.firstNameTooShort');
    } else if (profileData.firstName.trim().length > 50) {
      newErrors.firstName = t('profile.firstNameTooLong');
    }

    // Last name validation
    if (!profileData.lastName.trim()) {
      newErrors.lastName = t('profile.lastNameRequired');
    } else if (profileData.lastName.trim().length < 2) {
      newErrors.lastName = t('profile.lastNameTooShort');
    } else if (profileData.lastName.trim().length > 50) {
      newErrors.lastName = t('profile.lastNameTooLong');
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!profileData.email.trim()) {
      newErrors.email = t('profile.emailRequired');
    } else if (!emailRegex.test(profileData.email)) {
      newErrors.email = t('profile.emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof ProfileData, value: string) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      await updateProfile({
        firstName: profileData.firstName.trim(),
        lastName: profileData.lastName.trim(),
        email: profileData.email.trim(),
        gender: profileData.gender || undefined,
        nationality: profileData.nationality || null,
        birthYear: profileData.birthYear ? Number(profileData.birthYear) : null
      });

      setSuccessMessage(t('profile.updateSuccess'));
      setIsEditing(false);

      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (error: any) {
      console.error('Profile update error:', error);
      setErrors({ general: error.message || t('profile.updateError') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const cancelEdit = () => {
    if (user) {
      setProfileData({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user.email || '',
        gender: user?.gender || '',
        nationality: user?.nationality || '',
        birthYear: user?.birthYear ? String(user.birthYear) : ''
      });
    }
    setIsEditing(false);
    setErrors({});
    setSuccessMessage('');
  };
  if (!user) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-user-line text-6xl text-gray-400 mb-4"></i>
            <p className="text-gray-600">Please sign in to view your profile.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const accountCreatedAt = user.createdAt ? new Date(user.createdAt) : null;
  const emailVerified = user.emailVerified ?? Boolean(user.emailVerifiedAt);
  const lastLoginAt = user.lastLoginAt ? new Date(user.lastLoginAt) : null;



  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-[var(--pv-color-primary)]">{t('profile:profile.title')}</h1>
                <p className="text-gray-600 mt-1">{t('profile:profile.subtitle')}</p>
              </div>
              <div className="flex items-center space-x-3">
                {activeTab === 'profile' && !isEditing && (
                  <Button
                    onClick={() => setIsEditing(true)}
                    variant="outline"
                  >
                    <i className="ri-edit-line mr-2"></i>
                    {t('profile:profile.editProfile')}
                  </Button>
                )}
                {activeTab === 'profile' && isEditing && (
                  <Button
                    onClick={cancelEdit}
                    variant="outline"
                    disabled={isLoading}
                  >
                    {t('profile:profile.cancel')}
                  </Button>
                )}
                <Button
                  onClick={handleSignOut}
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <i className="ri-logout-circle-line mr-2"></i>
                  {t('profile:profile.signOut')}
                </Button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-4 mt-4">
              <button
                onClick={() => setActiveTab('profile')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'profile'
                    ? 'bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <i className="ri-user-line mr-2"></i>
                {t('profile:profile.profileTab')}
              </button>
              <button
                onClick={() => setActiveTab('favorites')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'favorites'
                    ? 'bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <i className="ri-heart-line mr-2"></i>
                {t('profile:profile.favoritesTab')} ({favorites.length})
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {successMessage && (
              <div className="mb-6 p-4 bg-green-100 border border-green-300 text-green-700 rounded-md">
                <i className="ri-check-line mr-2"></i>
                {successMessage}
              </div>
            )}

            {errors.general && (
              <div className="mb-6 p-4 bg-red-100 border border-red-300 text-red-700 rounded-md">
                <i className="ri-error-warning-line mr-2"></i>
                {errors.general}
              </div>
            )}

            {activeTab === 'profile' ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Profile Form */}
                <div className="lg:col-span-2">
                  {/* Avatar uploader — independent of the edit-mode form below. */}
                  <div className="mb-6 pb-6 border-b border-gray-100">
                    <AvatarUpload />
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('profile:profile.firstName')}
                        </label>
                        <Input
                          type="text"
                          value={profileData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          className={errors.firstName ? 'border-red-300' : ''}
                          disabled={!isEditing || isLoading}
                          maxLength={50}
                        />
                        {errors.firstName && (
                          <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('profile:profile.lastName')}
                        </label>
                        <Input
                          type="text"
                          value={profileData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          className={errors.lastName ? 'border-red-300' : ''}
                          disabled={!isEditing || isLoading}
                          maxLength={50}
                        />
                        {errors.lastName && (
                          <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('profile:profile.email')}
                      </label>
                      <Input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={errors.email ? 'border-red-300' : ''}
                        disabled={!isEditing || isLoading}
                      />
                      {errors.email && (
                        <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                      )}
                      {isEditing && (
                        <p className="text-gray-500 text-sm mt-1">
                          {t('profile:profile.emailChangeNote')}
                        </p>
                      )}
                    </div>

                    {/* Optional demographics (Statistics platform) — reuses registration namespace labels */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration:account.gender')}
                        </label>
                        <select
                          value={profileData.gender}
                          onChange={(e) => handleInputChange('gender', e.target.value)}
                          disabled={!isEditing || isLoading}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1B7566]/30 focus:border-[#004D40]"
                        >
                          <option value="">{t('registration:account.genderSelect')}</option>
                          <option value="MALE">{t('registration:account.genderMale')}</option>
                          <option value="FEMALE">{t('registration:account.genderFemale')}</option>                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration:account.nationality')}
                        </label>
                        <select
                          value={profileData.nationality}
                          onChange={(e) => handleInputChange('nationality', e.target.value)}
                          disabled={!isEditing || isLoading}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1B7566]/30 focus:border-[#004D40]"
                        >
                          <option value="">{t('registration:account.nationalitySelect')}</option>
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>{i18n.language === 'ar' ? c.ar : c.en}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('registration:account.birthYear')}
                        </label>
                        <select
                          value={profileData.birthYear}
                          onChange={(e) => handleInputChange('birthYear', e.target.value)}
                          disabled={!isEditing || isLoading}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 disabled:bg-gray-50 disabled:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1B7566]/30 focus:border-[#004D40]"
                        >
                          <option value="">{t('registration:account.birthYearSelect')}</option>
                          {PROFILE_BIRTH_YEARS.map((y) => (
                            <option key={y} value={String(y)}>{y}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {isEditing && (
                      <div className="flex space-x-4">
                        <Button
                          type="submit"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <i className="ri-loader-4-line animate-spin mr-2"></i>
                              {t('profile:profile.saving')}
                            </>
                          ) : (
                            <>
                              <i className="ri-save-line mr-2"></i>
                              {t('profile:profile.saveChanges')}
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </form>
                </div>

                {/* Account Info Sidebar */}
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {t('profile:profile.accountInfo')}
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('profile:profile.userId')}:</span>
                        <span className="font-mono text-xs">{user.id.slice(0, 8)}...</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('profile:profile.accountCreated')}:</span>
                        <span>{accountCreatedAt ? accountCreatedAt.toLocaleDateString(latnLocale()) : '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('profile:profile.emailVerified')}:</span>
                        <span className={emailVerified ? 'text-green-600' : 'text-yellow-600'}>
                          {emailVerified ? t('profile:profile.yes') : t('profile:profile.pending')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('profile:profile.lastSignIn')}:</span>
                        <span>{lastLoginAt ? lastLoginAt.toLocaleDateString(latnLocale()) : t('profile:profile.never')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('profile:profile.needHelp')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {t('profile:profile.helpText')}
                    </p>
                    <Button variant="outline" className="w-full" onClick={openChat}>
                      <i className="ri-customer-service-line mr-2"></i>
                      {t('profile:profile.contactSupport')}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              /* Favorites Tab */
              <div>
                {(favoritesLoading || loadingProperties) ? (
                  <div className="flex justify-center items-center py-12">
                    <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
                  </div>
                ) : favoriteProperties.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-heart-line text-6xl text-gray-300 mb-4"></i>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {t('profile:profile.noFavorites')}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {t('profile:profile.noFavoritesDescription')}
                    </p>
                    <Link to="/search">
                      <Button>
                        <i className="ri-search-line mr-2"></i>
                        {t('profile:profile.browseProperties')}
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {favoriteProperties.map((favorite) => (
                      <div
                        key={favorite.id}
                        className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                        dir={i18n.dir()}
                      >
                        <div className="flex">
                          {/* Property Image */}
                          <div className="w-32 h-32 flex-shrink-0 bg-gray-100">
                            {favorite.imageUrl ? (
                              <img
                                src={favorite.imageUrl}
                                alt={favorite.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <i className="ri-home-4-line text-3xl text-gray-400"></i>
                              </div>
                            )}
                          </div>

                          {/* Property Info */}
                          <div className="flex-1 p-4">
                            <h4 className="font-semibold text-gray-900 mb-1 line-clamp-1">
                              {favorite.title || t('profile:profile.unknownProperty')}
                            </h4>
                            {favorite.city && (
                              <p className="text-sm text-gray-600 mb-2">
                                <i className="ri-map-pin-line mr-1"></i>
                                {favorite.city}{favorite.state ? `, ${favorite.state}` : ''}
                              </p>
                            )}
                            {favorite.price > 0 && (
                              <p className="text-[#004D40] font-semibold flex items-center gap-1">
                                <SaudiRiyalSymbol className="h-4 w-4" />
                                {formatAmount(favorite.price)}
                              </p>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex flex-col justify-center gap-2 p-4">
                            <Link to={`/property/${favorite.propertyId}`}>
                              <Button size="sm" variant="outline">
                                <i className="ri-eye-line"></i>
                              </Button>
                            </Link>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRemoveFavorite(favorite.propertyId)}
                              disabled={removingFavorite === favorite.propertyId}
                              className="text-red-500 border-red-300 hover:bg-red-50"
                            >
                              {removingFavorite === favorite.propertyId ? (
                                <i className="ri-loader-4-line animate-spin"></i>
                              ) : (
                                <i className="ri-heart-fill"></i>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
